<template>
    <div class="equipment-control-setting">
        <div class="setting-content">
            <!-- 노드 선택 섹션 -->
            <div class="setting-section">
                <h5>{{ $t("노드 설정") }}</h5>

                <!-- 루트 노드 선택 -->
                <div class="form-group">
                    <label>{{ $t("설비 계통") }}</label>
                    <select v-model="selectedRootNode" class="form-control" @change="onRootNodeChange">
                        <option :value="null">{{ $t("루트 노드 선택") }}</option>
                        <option v-for="node in rootNodeList" :key="node.sysNodeIdx" :value="node.sysNodeIdx">
                            {{ node.sysNodeName }}
                        </option>
                    </select>
                </div>

                <!-- 설비 노드 선택 -->
                <div class="form-group equipment-node-field">
                    <label>{{ $t("설비 노드") }}</label>
                    <tree-select
                        v-model="selectedEquipmentNode"
                        class="equipment-node-tree-select"
                        :options="equipmentNodeTreeOptions"
                        :multiple="false"
                        :disabled="!selectedRootNode"
                        :placeholder="$t('설비 노드 선택')"
                        :no-children-text="$t('하위 노드 없음')"
                        :no-results-text="$t(controlMessages.COMMON_TREE_NOSEARCH_MESSAGE)"
                        :max-height="280"
                        @input="onEquipmentNodeChange"
                    />
                </div>

                <!-- 위젯 제목 -->
                <div class="form-group">
                    <label>{{ $t("위젯 제목") }}</label>
                    <input
                        v-model="widgetTitle"
                        type="text"
                        class="form-control"
                        :placeholder="$t('위젯 제목을 입력하세요')"
                    />
                </div>

                <!-- 표시 아이콘 -->
                <div class="form-group widget-icon-field">
                    <label>{{ $t("표시 아이콘") }}</label>
                    <p class="widget-icon-hint">{{ $t("이 위젯에만 적용됩니다. 설비유형 기본 아이콘과 별도로 지정할 수 있습니다.") }}</p>
                    <icon-select :current="selectedIcon" @on-change="onChangeIcon">
                        <span slot-scope="{ iconClass, iconText }" class="widget-icon-trigger">
                            <i :class="iconClass || 'fas fa-cog'">
                                <template v-if="iconText">{{ iconText }}</template>
                            </i>
                            <span class="widget-icon-trigger-label">{{ $t("아이콘 선택") }}</span>
                        </span>
                    </icon-select>
                </div>

                <!-- 표시 수준 -->
                <div class="form-group">
                    <label>{{ $t("표시 수준") }}</label>
                    <div class="display-mode-group">
                        <label class="display-mode-option" :class="{ active: displayMode === 'full' }">
                            <input v-model="displayMode" type="radio" value="full" />
                            <span>{{ $t("기본") }}</span> &nbsp;
                            <small>{{ $t("온도·모드·팬 등 전체 표시") }}</small>
                        </label>
                        <label class="display-mode-option" :class="{ active: displayMode === 'minimal' }">
                            <input v-model="displayMode" type="radio" value="minimal" />
                            <span>{{ $t("간략") }}</span> &nbsp;
                            <small>{{ $t("ON/OFF 상태만 표시") }}</small>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <!-- 버튼 영역 -->
        <div class="setting-actions">
            <button type="button" class="btn btn-secondary" @click="$emit('on-modal-cancel')">
                {{ $t("취소") }}
            </button>
            <button type="button" class="btn btn-primary" @click="onSave">
                {{ $t("저장") }}
            </button>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";
import * as controlMessages from "@src/consts/controlMessageConsts";

const NODE_PATH_SEPARATOR = " · ";

export default {
    name: "EquipmentControlSetting",
    components: {},
    props: {
        initSetting: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            controlMessages,
            rootNodeList: [],
            selectedRootNode: null,
            nodeList: [],
            equipmentNodeTreeOptions: [],
            selectedEquipmentNode: null,
            widgetTitle: "",
            selectedIcon: "material lightbulb",
            currentEquipStatus: null,
            equipTypeInfo: null,
            pointInfo: null,
            displayMode: "full",
        };
    },
    computed: {
        selectedNodeInfo() {
            if (!this.selectedEquipmentNode) return null;
            return this.nodeList.find((node) => node.sysNodeIdx === this.selectedEquipmentNode);
        },
    },
    mounted() {
        this.initData();
        this.loadRootNodeList();
    },
    methods: {
        initData() {
            // 기존 설정값으로 초기화
            if (this.initSetting) {
                this.selectedRootNode = this.initSetting.rootNodeIdx || null;
                this.selectedEquipmentNode = this.initSetting.equipmentNodeIdx || null;
                this.widgetTitle = this.initSetting.title || "";
                this.selectedIcon = this.initSetting.bgIcon || "material lightbulb";
                this.currentEquipStatus = this.initSetting.currentEquipStatus || null;
                this.equipTypeInfo = this.initSetting.equipTypeInfo || null;
                this.pointInfo = this.initSetting.pointInfo || null;
                this.displayMode = this.initSetting.displayMode || "full";
            } else {
                // 새로운 위젯 구성 시 위젯 제목을 빈 문자열로 초기화
                this.widgetTitle = "";
            }
        },

        async loadRootNodeList() {
            try {
                const result = await backEndApi.sysNode.getRootNodeList("Y");
                if (result.status === 200) {
                    const filterEquip = result.data.filter((node) => node.sumAvail === "N");
                    this.rootNodeList = filterEquip;

                    // 기존 설정이 있으면 해당 루트 노드의 하위 노드들 로드
                    if (this.selectedRootNode) {
                        await this.loadNodeTree(this.selectedRootNode);
                    }
                }
            } catch (error) {
                console.error("Error loading root node list:", error);
                this.$toast.error(this.$t("루트 노드 목록을 가져오는 데 실패했습니다."));
            }
        },

        async onRootNodeChange() {
            this.selectedEquipmentNode = null;
            this.nodeList = [];
            this.equipmentNodeTreeOptions = [];

            if (this.selectedRootNode) {
                await this.loadNodeTree(this.selectedRootNode);
            }
        },

        async loadNodeTree(rootNodeIdx) {
            try {
                const result = await backEndApi.sysNode.getSysNodeTree(rootNodeIdx);
                if (result.status === 200 && result.data) {
                    this.nodeList = [];
                    this.equipmentNodeTreeOptions = this.convertApiTreeToSelectOptions(result.data);
                }
            } catch (error) {
                console.error("Error loading node tree:", error);
                this.$toast.error(this.$t("노드 트리를 가져오는 데 실패했습니다."));
            }
        },

        convertApiTreeToSelectOptions(treeData) {
            if (!treeData) return [];

            const roots = Array.isArray(treeData) ? treeData : [treeData];
            const options = [];

            roots.forEach((root) => {
                if (root.nodeType === "Root") {
                    (root.children || []).forEach((child) => {
                        const option = this.buildTreeSelectNode(child, "", 0);
                        if (option) options.push(option);
                    });
                    return;
                }

                const option = this.buildTreeSelectNode(root, "", 0);
                if (option) options.push(option);
            });

            return options;
        },

        buildTreeSelectNode(node, parentPath = "", level = 0) {
            if (!node || node.nodeType === "Root") return null;

            const label = node.text || node.sysNodeName;
            this.nodeList.push({
                ...node,
                text: label,
                parentPath,
                level,
            });

            const option = {
                id: node.sysNodeIdx,
                label: `${label} (${node.nodeType})`,
            };

            const children = (node.children || []).filter((child) => child.nodeType !== "Root");
            if (children.length > 0) {
                const childParentPath = parentPath
                    ? `${parentPath}${NODE_PATH_SEPARATOR}${label}`
                    : label;
                option.children = children
                    .map((child) => this.buildTreeSelectNode(child, childParentPath, level + 1))
                    .filter(Boolean);
            }

            return option;
        },

        async onEquipmentNodeChange() {
            // 설비 노드가 변경되면 위젯 제목 자동 설정
            if (this.selectedEquipmentNode && this.selectedNodeInfo) {
                this.widgetTitle = this.selectedNodeInfo.text || "";
            }
        },

        onChangeIcon(value) {
            if (!this.isEmpty(value)) this.selectedIcon = value.icon;
            else this.selectedIcon = "";
        },

        async loadCurrentEquipStatus(nodeInfo) {
            if (!nodeInfo?.sysNodeIdx || !nodeInfo?.equipType) return;

            try {
                const result = await backEndApi.sysNode.getIncludedEquipStatus(nodeInfo.sysNodeIdx, nodeInfo.equipType);
                if (result.status === 200 && result.data && result.data.length > 0) {
                    if (nodeInfo.nodeType === "Group") this.currentEquipStatus = result.data;
                    else this.currentEquipStatus = result.data[0];
                } else {
                    console.warn("No equip status data available");
                    this.currentEquipStatus = null;
                }
            } catch (error) {
                console.error("Error loading current equip status:", error);
                this.currentEquipStatus = null;
            }
        },

        async loadEquipTypeInfo(nodeInfo) {
            if (!nodeInfo?.equipType) {
                this.equipTypeInfo = null;
                return;
            }

            try {
                const result = await backEndApi.equipTypes.getEquipType(nodeInfo.equipType, "Y");

                if (result.status === 200 && result.data) {
                    this.equipTypeInfo = result.data;
                } else {
                    console.warn("No equip type info available in modal");
                    this.equipTypeInfo = null;
                }
            } catch (error) {
                console.error("Error loading equip type info in modal:", error);
                this.equipTypeInfo = null;
            }
        },

        async getPointInfo() {
            try {
                const ptIdx =
                    this.selectedNodeInfo.nodeType === "Group"
                        ? this.currentEquipStatus[0].ioProps[0].ptIdx
                        : this.currentEquipStatus.ioProps[0].ptIdx;
                const result = await backEndApi.pointInfo.getPointDetailNew(ptIdx);
                if (result.status === 200 && result.data) {
                    this.pointInfo = {
                        serverIdx: result.data.serverIdx,
                        channelIdx: result.data.channelIdx,
                    };
                } else {
                    console.warn("No point info available");
                    this.pointInfo = null;
                }
            } catch (error) {
                console.error("Error loading point info:", error);
                this.pointInfo = null;
            }
        },

        async onSave() {
            if (!this.isEmpty(this.selectedNodeInfo)) {
                // equipTypeInfo·currentEquipStatus 는 저장하지 않지만, pointInfo(serverIdx·channelIdx)
                // 산출에 필요하므로 로컬에서만 조회한다. (위젯은 런타임에 공유 캐시로 다시 가져온다)
                await this.loadCurrentEquipStatus(this.selectedNodeInfo);
                await this.getPointInfo();
            }

            // 저장본에는 바인딩 키만 남긴다. nodeInfo.children(전체 설비 트리)도 제외.
            const nodeInfo = this.selectedNodeInfo ? { ...this.selectedNodeInfo } : null;
            if (nodeInfo) delete nodeInfo.children;

            const setting = {
                rootNodeIdx: this.selectedRootNode,
                equipmentNodeIdx: this.selectedEquipmentNode,
                title: this.widgetTitle || this.selectedNodeInfo?.text || "설비 제어",
                bgIcon: this.selectedIcon,
                nodeInfo,
                pointInfo: this.pointInfo,
                displayMode: this.displayMode,
                panel: {
                    backgroundColor: this.initSetting?.panel?.backgroundColor,
                },
            };

            this.$emit("on-modal-ok", setting);
        },
    },
};
</script>

<style scoped>
.equipment-control-setting {
    padding: 20px;
}

.setting-content {
    margin-bottom: 20px;
}

.setting-section {
    margin-bottom: 30px;
    padding: 20px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #f8f9fa;
}

.setting-section h5 {
    margin-bottom: 20px;
    color: #333;
    font-weight: 600;
    border-bottom: 2px solid #007bff;
    padding-bottom: 8px;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #555;
}

.form-control {
    width: 100%;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.3s ease;
}

.form-control:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.equipment-node-field >>> .vue-treeselect {
    font-size: 14px;
}

.equipment-node-field >>> .vue-treeselect__control {
    min-height: 38px;
    border-color: #ddd;
    border-radius: 6px;
}

.equipment-node-field >>> .vue-treeselect__control:hover {
    border-color: #bbb;
}

.equipment-node-field >>> .vue-treeselect--focused .vue-treeselect__control {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.equipment-node-field >>> .vue-treeselect__placeholder,
.equipment-node-field >>> .vue-treeselect__single-value {
    font-size: 14px;
}

.equipment-info {
    background: white;
    border-radius: 6px;
    padding: 15px;
    border: 1px solid #e0e0e0;
}

.info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
}

.info-item:last-child {
    border-bottom: none;
}

.info-item .label {
    font-weight: 600;
    color: #555;
    min-width: 80px;
}

.info-item .value {
    color: #333;
    font-weight: 500;
}

.display-mode-group {
    display: flex;
    gap: 10px;
}

.display-mode-option {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 10px 8px;
    border: 1px solid #ddd;
    border-radius: 8px;
    cursor: pointer;
    text-align: center;
    font-weight: 500;
    color: #555;
    transition:
        border-color 0.2s,
        background 0.2s;
    margin: 0;
}

.display-mode-option input {
    display: none;
}

.display-mode-option.active {
    border-color: #007bff;
    background: #f0f6ff;
    color: #0056b3;
}

.display-mode-option .mode-icon {
    font-size: 18px;
    opacity: 0.7;
}

.display-mode-option small {
    font-size: 10px;
    color: #888;
    font-weight: 400;
}

.widget-icon-hint {
    margin: 0 0 8px;
    font-size: 11px;
    color: #6b7280;
}

.widget-icon-trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
    color: #374151;
}

.widget-icon-trigger:hover {
    border-color: #9ca3af;
    background: #f9fafb;
}

.widget-icon-trigger i {
    font-size: 1.1rem;
}

.widget-icon-trigger-label {
    font-size: 12px;
}

.setting-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
}

.btn {
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 600;
    transition: all 0.3s ease;
    border: none;
    cursor: pointer;
}

.btn-primary {
    background-color: #007bff;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background-color: #0056b3;
}

.btn-primary:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background-color: #545b62;
}
</style>
